import { Component, DestroyRef, HostListener, OnInit, inject } from '@angular/core';
import { SmartphotoService } from '../../services/smartphoto.service';
import { StatusColor, StatusIcon, StatusLang, StatusMap } from '../../entities/enums/status.enum';
import { Subject, debounceTime, firstValueFrom } from 'rxjs';
import { BdeoInput } from '@bdeo/webcomponents';
import { CompanyService } from '../../services/company.service';
import { orderByType } from '../../entities/types/order-by.type';
import { Filter } from 'src/app/entities/types/filters.type';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Company, Smartphoto } from '@bdeo-motor/smartphoto-entities';

@Component({
  selector: 'app-smartphoto-list',
  templateUrl: './smartphoto-list.component.html',
  styleUrls: ['./smartphoto-list.component.scss']
})
export class SmartphotoListComponent implements OnInit {
  private readonly smartphotoService = inject(SmartphotoService);
  private readonly companyService = inject(CompanyService);
  private readonly destroyRef = inject(DestroyRef);

  private searchSubject$ = new Subject<string>();
  private canLoadList = true;
  private scrollBefore = 0;
  private order: orderByType = 'createdAt';

  public activeStatuses: number[] = [];
  public smartphotoList: Smartphoto[] = [];
  public loading = true;
  public currentPage = 0;
  public searchInput = '';
  public sticky = true;
  public showCreateButton = false;
  public showGoBack = false;
  public filters: Filter[] = [];

  @HostListener('window:scroll')
  async onScroll(): Promise<void> {
    const scrollHeight = document.body.scrollHeight - window.innerHeight;
    const scrollTop = window.scrollY;
    const scrollPercentage = (scrollTop / scrollHeight) * 100;

    if (scrollPercentage >= 90 && this.canLoadList) {
      this.currentPage++;
      await this.getNextPage();
    }

    if (this.scrollBefore > scrollTop) {
      setTimeout(() => {
        this.sticky = true;
        this.scrollBefore = scrollTop;
      }, 200);
    } else if (scrollTop > 50) {
      this.sticky = false;
      this.scrollBefore = scrollTop;
    }
  }

  public async ngOnInit(): Promise<void> {
    const currentCompany = this.companyService.getCurrentCompany();

    if (!currentCompany) {
      this.companyService
        .getCurrentCompanyObservable()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(async (company) => {
          if (!company) {
            return;
          }

          await this.init(company);
        });
    } else {
      await this.init(currentCompany);
    }

    this.searchSubject$.pipe(takeUntilDestroyed(this.destroyRef), debounceTime(300)).subscribe(async () => {
      await this.updateSmartphotoList();
    });
  }

  private async init(company: Company): Promise<void> {
    this.order = company.deadlineSelfadjust ? 'deadlineSelfadjust' : 'createdAt';
    this.smartphotoList = await this.smartphotoService.get.list(0, this.order, false);
    this.filters = await this.getFilters();
    this.loading = false;
    this.showCreateButton = company.brokersInfo?.showCreateButton ?? false;
  }

  public async getFilters(): Promise<Filter[]> {
    const allFilters = Object.keys(StatusLang).map((key) => ({
      text: StatusLang[parseInt(key as string) as keyof StatusMap],
      icon: StatusIcon[parseInt(key as string) as keyof StatusMap],
      color: StatusColor[parseInt(key as string) as keyof StatusMap],
      status: parseInt(key)
    }));

    const hasAdditionals = await firstValueFrom(this.companyService.hasAdditionals$);

    if (!hasAdditionals) {
      return allFilters.filter((filter) => filter.text !== StatusLang[8] && filter.text !== StatusLang[7]);
    }

    return allFilters;
  }

  private async updateSmartphotoList(): Promise<void> {
    this.loading = true;
    this.smartphotoList = await this.getSmartphotoList(this.order);
    this.loading = false;
  }

  private async getNextPage(): Promise<void> {
    this.canLoadList = false;
    const nextPageList = await this.getSmartphotoList('createdAt');

    if (!nextPageList.length) {
      return;
    }

    this.smartphotoList = [...this.smartphotoList, ...nextPageList];
    this.canLoadList = true;
  }

  private async getSmartphotoList(order: orderByType): Promise<Smartphoto[]> {
    return this.smartphotoService.get.list(this.currentPage, order, false, {
      status: this.activeStatuses,
      caseRef: this.searchInput
    });
  }

  public async toggleFilter(status: number): Promise<void> {
    if (this.activeStatuses.includes(status)) {
      this.activeStatuses.splice(this.activeStatuses.indexOf(status), 1);
    } else {
      this.activeStatuses.push(status);
    }

    this.currentPage = 0;
    this.canLoadList = true;

    await this.updateSmartphotoList();
  }

  public async searchInputChange(event: Event): Promise<void> {
    if (event.target instanceof BdeoInput && event.target.value) {
      this.searchInput = event.target.value;
      if (this.searchInput.length > 1) {
        this.searchSubject$.next(this.searchInput);
        this.showGoBack = true;
      }
    } else {
      await this.resetSearch();
    }
  }

  public async resetSearch(): Promise<void> {
    this.searchInput = '';
    this.showGoBack = false;
    await this.updateSmartphotoList();
  }

  public cleanSearchInput(): void {
    this.searchInput = '';
  }
}
